import {ClientAnalytics, Link, useNavigate} from '@shopify/hydrogen';
import {ReactElement, useCallback, useEffect, useMemo} from 'react';
import {useQuery as useBackendQuery} from 'react-query';
import {AxiosError} from 'axios';

import {
  deleteMyCollectedProduct,
  getMyCollectedProduct,
  MemberCollectedProductUpdatePayloadType,
  updateMyCollectedProduct,
} from '../../backendApi/my-products';

import useFormState, {ErrorType} from '../../hooks/useFormState.client';
import {
  MemberCollectedProductStatusType,
  MemberCollectedProductType,
  MemberCollectedProductWhenToOpen,
  MemberCollectedProductDisposedReason,
  MemberProductSaleType,
} from '../../types/index';
import {useAuthService} from '../../contexts/AuthServiceProvider.client';
import CheckboxField from '../formControls/CheckboxField.client';
import InputField from '../formControls/InputField.client';
import SingleSelectField from '../formControls/SingleSelectField.client';
import TextAreaField from '../formControls/TextAreaField.client';

import SwalComponent from '../modals/SwalComponent.client';
import FlexibleDateMonthYearField from '../formControls/FlexibleDateMonthYearField.client';
import NumberInputSliderField from '../formControls/NumberInputSliderField.client';
import FileInputField from '../formControls/FileInputField.client';
import {IndexPageViewType, TwlAnalyticsEvent} from '../../constants';

const fieldsToCopy = [
  'uuid',
  'friendly_name',
  'volume_ml',
  'batch_or_cask_no',
  'bottle_number',
  'bottles_in_release',
  'distilled_date',
  'distilled_date_type',
  'bottled_date',
  'bottled_date_type',
  'acquisition_date',
  'acquisition_date_type',
  'acquisition_value',
  'acquired_from',
  'status',
  'closed_when_to_open',
  'open_fill_level',
  'open_date',
  'open_date_type',
  'disposed_reason',
  'disposed_date',
  'disposed_date_type',
  'no_of_bottles',
  'est_per_bottle_value',
  'location_stored',
  'notes',
  'is_available_for_sale',
  'sale_units_available',
  'sale_asking_price',
  'sale_reserve_price',
  'sale_type',
  'sale_terms_accepted_at',
  'image',
  'receipt',
];

interface MemberCollectedProductFormProps {
  uuid: string;
  isProductRequest: boolean;
  isNewRecord: boolean;
  onSuccess?: (memberCollectedProduct: MemberCollectedProductType) => void;
  onError?: (error: AxiosError) => void;
}

export default function MemberCollectedProductForm({
  uuid,
  isNewRecord,
  onSuccess,
  onError,
}: MemberCollectedProductFormProps): ReactElement {
  const navigate = useNavigate();
  const {authUser} = useAuthService();

  const isRewardsMember = useMemo(
    () => authUser && Boolean(authUser?.member?.is_rewards_member),
    [authUser],
  );

  const {data: myMemberCollectedProduct, refetch} = useBackendQuery(
    ['myMemberCollectedProduct', uuid],
    () => getMyCollectedProduct(uuid),
    {retry: 1, enabled: true},
  );

  const {formFields, fieldErrors, setFormFields, handleChange, handleSubmit} =
    useFormState<
      MemberCollectedProductUpdatePayloadType,
      MemberCollectedProductType,
      ErrorType<MemberCollectedProductType>
    >(
      updateMyCollectedProduct,
      {
        uuid: '',
        friendly_name: '',
        volume_ml: '',
        batch_or_cask_no: '',
        bottle_number: '',
        bottles_in_release: '',
        distilled_date: '', // DD-MM-YYYY
        distilled_date_type: undefined,
        bottled_date: '', // DD-MM-YYYY
        bottled_date_type: undefined,
        acquisition_date: '', // DD-MM-YYYY
        acquisition_date_type: undefined,
        acquisition_value: '',
        acquired_from: '',
        status: MemberCollectedProductStatusType.closed.value, // Dropdown
        closed_when_to_open: '', // Dropdown
        open_fill_level: '',
        open_date: '', // DD-MM-YYYY
        open_date_type: undefined,
        disposed_reason: '', // Dropdown
        disposed_date: '', // DD-MM-YYYY
        disposed_date_type: undefined,
        no_of_bottles: '',
        est_per_bottle_value: '',
        location_stored: '',
        notes: '',
        is_available_for_sale: undefined,
        sale_units_available: '',
        sale_asking_price: '',
        sale_reserve_price: '',
        sale_type: '', // Dropdown
        image: undefined,
        receipt: undefined,
      },
      {
        onError,
        onSuccess: (memberCollectedProduct: MemberCollectedProductType) => {
          refetch();
          onSuccess?.(memberCollectedProduct);
          SwalComponent.close();
          SwalComponent.fire({
            title: 'Collected Product Saved',
            icon: 'success',
            html: (
              <button
                type="button"
                className="button_type_ghost button ml-10"
                onClick={() => {
                  SwalComponent.close();
                  const productId = myMemberCollectedProduct?.product?.uuid;
                  navigate(
                    `/my/collection?view=${IndexPageViewType.list}${
                      productId ? `&product_id=${productId}` : ''
                    }`,
                  );
                }}
              >
                View My Collection
              </button>
            ),
          });
        },
      },
    );

  useEffect(() => {
    if (myMemberCollectedProduct) {
      const updatedFields = fieldsToCopy.reduce((acc, field) => {
        if (myMemberCollectedProduct[field] !== null) {
          acc[field] = myMemberCollectedProduct[field];
        }
        return acc;
      }, {});
      setFormFields((prev) => ({
        ...prev,
        ...updatedFields,
      }));
    }
  }, [myMemberCollectedProduct, setFormFields]);

  const isBottleClosed = useMemo<boolean>(
    () => formFields?.status === MemberCollectedProductStatusType.closed.value,
    [formFields?.status],
  );

  const isBottleOpen = useMemo<boolean>(
    () => formFields?.status === MemberCollectedProductStatusType.open.value,
    [formFields?.status],
  );

  const isBottleDisposed = useMemo<boolean>(
    () =>
      formFields?.status === MemberCollectedProductStatusType.disposed.value,
    [formFields?.status],
  );

  const isSaleSectionVisible = useMemo<boolean>(
    () => (!isBottleDisposed && formFields?.is_available_for_sale) ?? false,
    [isBottleDisposed, formFields?.is_available_for_sale],
  );

  const handleKeyValueSetFormFields = useCallback(
    (key: keyof MemberCollectedProductUpdatePayloadType, value: unknown) => {
      setFormFields({
        ...formFields,
        [key]: value,
      });
    },
    [formFields, setFormFields],
  );

  const productTitle = useMemo(() => {
    if (!myMemberCollectedProduct) return <></>;
    const {product, product_request} = myMemberCollectedProduct;
    if (product) {
      return <Link to={`/shop/${product.handle}`}>{product?.title}</Link>;
    } else if (product_request) {
      return <>{product_request?.title}</>;
    }
    return <></>;
  }, [myMemberCollectedProduct]);

  return (
    <div className="row mt-25">
      <div className="col">
        <form className="form" onSubmit={handleSubmit}>
          <h3 className="title-2 mt-10">
            Add Details {productTitle && <>for {productTitle}</>}
          </h3>
          <div className="separator mt-20 mb-25"></div>
          <div>Please enter the details of the collected product.</div>
          <div className="form__row row">
            <SingleSelectField
              labelClassName="col-sm-12 mt-20"
              label="Bottle Status"
              name="status"
              value={formFields?.status ?? ''}
              error={fieldErrors?.status}
              onChange={handleChange}
              labeledOptions={MemberCollectedProductStatusType}
            />

            {/* NOTE: Only visible if bottle status is `closed` */}
            <SingleSelectField
              labelClassName="col-sm-12 mt-20"
              label="When are you planning to open?"
              name="closed_when_to_open"
              value={formFields?.closed_when_to_open ?? ''}
              error={fieldErrors?.closed_when_to_open}
              onChange={handleChange}
              labeledOptions={MemberCollectedProductWhenToOpen}
              hidden={!isBottleClosed}
              disabled={!isRewardsMember}
            />

            {/* NOTE: Only visible if bottle status is `open` */}
            <NumberInputSliderField
              labelClassName="col-sm-12 mt-20"
              label={'Open Fill Level (0-95%)'}
              name="open_fill_level"
              value={formFields?.open_fill_level}
              error={fieldErrors?.open_fill_level}
              onChange={(name, value) => {
                setFormFields({
                  ...formFields,
                  [name]: value,
                });
              }}
              min={5}
              max={95}
              step={5}
              hidden={!isBottleOpen}
              disabled={!isRewardsMember}
            />
            <FlexibleDateMonthYearField
              name="open_date"
              label="Open Date"
              value={{
                date: formFields?.open_date,
                dateType: formFields?.open_date_type,
              }}
              error={fieldErrors?.open_date}
              onChange={(dateStr, dateType) => {
                setFormFields({
                  ...formFields,
                  open_date: dateStr,
                  open_date_type: dateType,
                });
              }}
              hidden={!isBottleOpen}
              disabled={!isRewardsMember}
            />

            {/* NOTE: Only visible if bottle status is `disposed` */}
            <SingleSelectField
              labelClassName="col-sm-12 mt-20"
              label="Disposed Reason"
              name="disposed_reason"
              value={formFields?.disposed_reason ?? ''}
              error={fieldErrors?.disposed_reason}
              onChange={handleChange}
              labeledOptions={MemberCollectedProductDisposedReason}
              hidden={!isBottleDisposed}
              disabled={!isRewardsMember}
            />
            <FlexibleDateMonthYearField
              name="disposed_date"
              label="Disposed Date"
              value={{
                date: formFields?.disposed_date,
                dateType: formFields?.disposed_date_type,
              }}
              error={fieldErrors?.disposed_date}
              onChange={(dateStr, dateType) => {
                setFormFields({
                  ...formFields,
                  disposed_date: dateStr,
                  disposed_date_type: dateType,
                });
              }}
              hidden={!isBottleDisposed}
              disabled={!isRewardsMember}
            />

            <InputField
              labelClassName="col-sm-12 mt-20"
              label="Friendly Name"
              name="friendly_name"
              value={formFields?.friendly_name ?? ''}
              error={fieldErrors?.friendly_name}
              onChange={handleChange}
              disabled={!isRewardsMember}
            />
            <InputField
              labelClassName="col-sm-12 mt-20"
              label="Volume (ml)"
              name="volume_ml"
              type="number"
              min={1}
              value={formFields?.volume_ml ?? ''}
              error={fieldErrors?.volume_ml}
              onChange={handleChange}
              onWheel={(e) => e.currentTarget.blur()}
              disabled={!isRewardsMember}
            />
            <InputField
              labelClassName="col-sm-12 mt-20"
              label="Batch or Cask Number"
              name="batch_or_cask_no"
              value={formFields?.batch_or_cask_no ?? ''}
              error={fieldErrors?.batch_or_cask_no}
              onChange={handleChange}
              disabled={!isRewardsMember}
            />

            {!isRewardsMember ? (
              <div className="cta-section container align-center pt-50 pb-40">
                <h3 className="title-1 c-accent pb-20">
                  <strong>Join TWL Rewards</strong>
                </h3>

                <div className="font-20 mt-20">
                  <p>
                    Track your collection in all its glory and full detail by
                    joining TWL Rewards.
                  </p>
                  Plus get free shipping, special deals, points and more!
                </div>

                <Link
                  to="/l/rewards"
                  className="button button_type_ghost button_type_ghost_dark button_size_large mt-40"
                >
                  Join Now
                </Link>
              </div>
            ) : (
              <>
                <InputField
                  labelClassName="col-sm-12 mt-20"
                  label="Bottle Number"
                  name="bottle_number"
                  value={formFields?.bottle_number ?? ''}
                  error={fieldErrors?.bottle_number}
                  onChange={handleChange}
                />
                <InputField
                  labelClassName="col-sm-12 mt-20"
                  label="Number of bottles Released"
                  type="number"
                  name="bottles_in_release"
                  min={1}
                  value={formFields?.bottles_in_release ?? ''}
                  error={fieldErrors?.bottles_in_release}
                  onChange={handleChange}
                  onWheel={(e) => e.currentTarget.blur()}
                />
                <FlexibleDateMonthYearField
                  name="distilled_date"
                  label="Distilled Date"
                  value={{
                    date: formFields?.distilled_date,
                    dateType: formFields?.distilled_date_type,
                  }}
                  error={fieldErrors?.distilled_date}
                  onChange={(dateStr, dateType) => {
                    setFormFields({
                      ...formFields,
                      distilled_date: dateStr,
                      distilled_date_type: dateType,
                    });
                  }}
                />
                <FlexibleDateMonthYearField
                  name="bottled_date"
                  label="Bottled Date"
                  value={{
                    date: formFields?.bottled_date,
                    dateType: formFields?.bottled_date_type,
                  }}
                  error={fieldErrors?.bottled_date}
                  onChange={(dateStr, dateType) => {
                    setFormFields({
                      ...formFields,
                      bottled_date: dateStr,
                      bottled_date_type: dateType,
                    });
                  }}
                />
                <FlexibleDateMonthYearField
                  name="acquisition_date"
                  label="Acquisition Date"
                  value={{
                    date: formFields?.acquisition_date,
                    dateType: formFields?.acquisition_date_type,
                  }}
                  error={fieldErrors?.acquisition_date}
                  onChange={(dateStr, dateType) => {
                    setFormFields({
                      ...formFields,
                      acquisition_date: dateStr,
                      acquisition_date_type: dateType,
                    });
                  }}
                />
                <InputField
                  labelClassName="col-sm-12 mt-20"
                  label="Acquisition Value"
                  name="acquisition_value"
                  value={formFields?.acquisition_value ?? ''}
                  error={fieldErrors?.acquisition_value}
                  onChange={handleChange}
                />
                <InputField
                  labelClassName="col-sm-12 mt-20"
                  label="Acquired From"
                  name="acquired_from"
                  placeholder="How did you acquire this bottle?"
                  value={formFields?.acquired_from ?? ''}
                  error={fieldErrors?.acquired_from}
                  onChange={handleChange}
                />

                <InputField
                  labelClassName="col-sm-12 mt-20"
                  label="Number of Bottles"
                  name="no_of_bottles"
                  type="number"
                  min={1}
                  value={formFields?.no_of_bottles ?? ''}
                  error={fieldErrors?.no_of_bottles}
                  onChange={handleChange}
                  onWheel={(e) => e.currentTarget.blur()}
                />

                <InputField
                  labelClassName="col-sm-12 mt-20"
                  label="Current Value Per Bottle"
                  name="est_per_bottle_value"
                  value={formFields?.est_per_bottle_value ?? ''}
                  error={fieldErrors?.est_per_bottle_value}
                  onChange={handleChange}
                />

                <InputField
                  labelClassName="col-sm-12 mt-20"
                  label="Location Stored"
                  name="location_stored"
                  placeholder="Where is the bottle stored?"
                  value={formFields?.location_stored ?? ''}
                  error={fieldErrors?.location_stored}
                  onChange={handleChange}
                />

                <TextAreaField
                  labelClassName="col-sm-12 mt-20"
                  label="Notes"
                  name="notes"
                  value={formFields?.notes ?? ''}
                  error={fieldErrors?.notes}
                  onChange={handleChange}
                />

                {/* Attachment fields ---/ */}
                <FileInputField
                  labelClassName="col-sm-6 mt-20"
                  label="Image"
                  name="image"
                  value={formFields?.image}
                  onChange={handleKeyValueSetFormFields}
                  error={fieldErrors?.image}
                />

                <FileInputField
                  labelClassName="col-sm-6 mt-20"
                  label="Receipt"
                  name="receipt"
                  value={formFields?.receipt}
                  onChange={handleKeyValueSetFormFields}
                  error={fieldErrors?.receipt}
                />
                {/* /--- Attachment fields */}

                {(isBottleClosed || isBottleOpen) && (
                  <CheckboxField
                    labelClassName="col-sm-12 mt-20"
                    name="is_available_for_sale"
                    checked={formFields?.is_available_for_sale as boolean}
                    onChange={(e) => {
                      setFormFields({
                        ...formFields,
                        is_available_for_sale: e.target.checked,
                      });
                    }}
                  >
                    Available for Sale ?
                  </CheckboxField>
                )}

                {isSaleSectionVisible && (
                  <>
                    <InputField
                      labelClassName="col-sm-12 mt-20"
                      label="Sale Units Available"
                      name="sale_units_available"
                      type="number"
                      min={1}
                      value={formFields?.sale_units_available ?? ''}
                      error={fieldErrors?.sale_units_available}
                      onChange={handleChange}
                      onWheel={(e) => e.currentTarget.blur()}
                      required
                    />

                    <InputField
                      labelClassName="col-sm-12 mt-20"
                      label="Sale Asking Price"
                      name="sale_asking_price"
                      value={formFields?.sale_asking_price ?? ''}
                      error={fieldErrors?.sale_asking_price}
                      onChange={handleChange}
                      required
                    />

                    <InputField
                      labelClassName="col-sm-12 mt-20"
                      label="Sale Reserve Price"
                      name="sale_reserve_price"
                      value={formFields?.sale_reserve_price ?? ''}
                      error={fieldErrors?.sale_reserve_price}
                      onChange={handleChange}
                      required
                    />

                    <SingleSelectField
                      labelClassName="col-sm-12 mt-20"
                      label="Sale Store Credit or Cash"
                      name="sale_type"
                      value={formFields?.sale_type}
                      error={fieldErrors?.sale_type}
                      onChange={handleChange}
                      labeledOptions={MemberProductSaleType}
                    />

                    <CheckboxField
                      labelClassName="col-sm-12 mt-20"
                      name="sale_terms_accepted_at"
                      checked={Boolean(formFields?.sale_terms_accepted_at)}
                      error={fieldErrors?.sale_terms_accepted_at}
                      onChange={(e) => {
                        setFormFields({
                          ...formFields,
                          sale_terms_accepted_at: e.target.checked
                            ? new Date().toISOString()
                            : '',
                        });
                      }}
                    >
                      Accept{' '}
                      <Link
                        to="/news/selling-your-collection-terms-conditions/"
                        target="_blank"
                      >
                        Terms and Conditions
                      </Link>{' '}
                      for selling this product.
                    </CheckboxField>
                  </>
                )}
              </>
            )}

            <div className="col-sm-12 mt-30">
              <button className="button button_full">Save</button>
            </div>
            {!isNewRecord && !isBottleDisposed && (
              <div className="col-sm-12 mt-30">
                <button
                  type="button"
                  className="button button_full button_color_gray_1"
                  onClick={() => {
                    SwalComponent.fire({
                      title: 'Remove Product from collection?',
                      icon: 'warning',
                      html: (
                        <>
                          <p className="title-3 mb-30">
                            This will mark the product as disposed from your
                            collection.
                          </p>
                          <button
                            className="button"
                            type="button"
                            onClick={async () => {
                              try {
                                await deleteMyCollectedProduct(uuid);
                                ClientAnalytics.publish(
                                  TwlAnalyticsEvent.REMOVE_FROM_COLLECTION,
                                  true,
                                  {
                                    ...ClientAnalytics.getPageAnalyticsData(),
                                    productId:
                                      myMemberCollectedProduct?.product?.uuid,
                                  },
                                );
                                SwalComponent.fire({
                                  title: 'Success!',
                                  icon: 'success',
                                  text: 'Product removed from collection.',
                                  showConfirmButton: true,
                                }).then(() => {
                                  SwalComponent.close();
                                  onSuccess?.(myMemberCollectedProduct);
                                });
                              } catch (error) {
                                SwalComponent.fire({
                                  title: 'Error!',
                                  icon: 'error',
                                  text: 'Failed to remove product from collection.',
                                });
                              }
                            }}
                          >
                            Confirm
                          </button>
                          <button
                            className="button button_color_gray_1 ml-10"
                            type="button"
                            onClick={() => SwalComponent.close()}
                          >
                            Cancel
                          </button>
                        </>
                      ),
                    });
                  }}
                >
                  Remove from collection
                </button>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
